import React, { useState, useEffect } from 'react';
import Modal from '../admin/Modal';
import { api } from '../../lib/api';

const CHANNELS = [
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['in_person', 'In person'],
  ['sms', 'SMS'],
  ['video_call', 'Video call'],
  ['other', 'Other'],
];

function nowSqlite() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Free-interaction logger — used by AccountDetail's Timeline tab to capture
// ad-hoc notes (calls, walk-ins, follow-ups) that aren't tied to a stage
// transition. Stage-changing flows now go through StageTransitionDialog so
// per-stage structured fields land in `interactions.structured_payload_json`.
// `initial` prefills the form when it opens (the Calendar page passes the
// meeting's time, title and contact): { channel, direction, occurredAt, subject, summary, contactId }.
export default function InteractionLogger({
  open, onClose, accountId,
  onLogged,
  title = null,
  initial = null,
}) {
  const [form, setForm] = useState({
    channel: 'phone',
    direction: 'outbound',
    occurredAt: nowSqlite(),
    subject: '',
    summary: '',
    followUpNeeded: false,
    followUpNotes: '',
    followUpAt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm((s) => ({
        ...s, occurredAt: nowSqlite(), summary: '', subject: '', followUpNotes: '', followUpAt: '',
        ...(initial ? Object.fromEntries(Object.entries(initial).filter(([, v]) => v != null && v !== '')) : {}),
      }));
      setError('');
    }
  }, [open, initial]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`/api/admin/accounts/${accountId}/interactions`, {
        channel: form.channel,
        direction: form.direction,
        occurredAt: form.occurredAt,
        subject: form.subject.trim() || null,
        summary: form.summary.trim(),
        followUpNeeded: form.followUpNeeded,
        followUpNotes: form.followUpNotes.trim() || null,
        // A date makes the follow-up a task due that day (for whoever logs this).
        followUpAt: form.followUpNeeded && form.followUpAt ? form.followUpAt : null,
        ...(form.contactId ? { contactId: form.contactId } : {}),
      });
      onLogged?.(res);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title ?? 'Log interaction'}>
      <div className="space-y-4">
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Channel">
            <select className={inputCls} value={form.channel} onChange={(e) => set('channel', e.target.value)}>
              {CHANNELS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
          </Field>
          <Field label="Occurred at">
            <input className={inputCls} value={form.occurredAt} onChange={(e) => set('occurredAt', e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
          </Field>
        </div>
        <Field label="Direction">
          <div className="flex items-center gap-2">
            {['outbound', 'inbound'].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => set('direction', d)}
                className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${
                  form.direction === d
                    ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-[#B0FC38]'
                    : 'bg-white/[0.02] border-white/[0.08] text-gray-400 hover:text-white'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Subject (optional)">
          <input className={inputCls} value={form.subject} onChange={(e) => set('subject', e.target.value)} />
        </Field>
        <Field label="Summary">
          <textarea autoFocus rows={4} className={inputCls} value={form.summary} onChange={(e) => set('summary', e.target.value)} placeholder="What was discussed?" />
        </Field>
        <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 select-none cursor-pointer">
          <input type="checkbox" checked={form.followUpNeeded} onChange={(e) => set('followUpNeeded', e.target.checked)} className="accent-[#B0FC38]" />
          Follow-up needed
        </label>
        {form.followUpNeeded && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Follow-up notes">
              <input className={inputCls} value={form.followUpNotes} onChange={(e) => set('followUpNotes', e.target.value)} />
            </Field>
            <Field label="Follow up on">
              <input type="date" className={inputCls} value={form.followUpAt} onChange={(e) => set('followUpAt', e.target.value)} data-testid="follow-up-date" />
              <div className="text-[10.5px] text-gray-500 mt-1 normal-case tracking-normal">Pick a date to get a task and a reminder that day.</div>
            </Field>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !form.summary.trim()}
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Log interaction'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      {children}
    </label>
  );
}
